// ── OrderRecommendCard — Dashboard 발주 추천 카드 ──
// 재고 + 사용 속도 + 안전재고 데이터로 발주 우선순위 Top 5 노출.
// 카드 클릭 → 발주서 페이지로 이동 (원클릭 발주서 추가).

import React, { useMemo } from 'react';
import { getOrderRecommendations, calcAvgUsage, mergeUsage } from '../utils/inventoryAlerts';
import { calcRecipeBasedUsage } from '../utils/recipeUsage';

// 세이지+코랄 팔레트
const SAGE = '#EA580C';
const SAGE_DARK = '#2C3A30';
const SAGE_BG = '#E0EAE2';
const CORAL = '#F4A89A';
const CORAL_DARK = '#B55041';
const WARM_BG = '#F7F4ED';
const WHITE = '#FFFFFF';
const MUTED = '#6B7A71';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function OrderRecommendCard({
  prices = [],
  inventory = {},
  orderHistory = [],
  categories = [],
  onNavigate,
  isMobile,
}) {
  const recommendations = useMemo(() => {
    // 거래 발주 이력 + 레시피 기반 예측 병합
    const orderUsage = calcAvgUsage(orderHistory, 3);
    const recipeUsage = calcRecipeBasedUsage
      ? (calcRecipeBasedUsage(categories, []) || {})
      : {};
    const merged = mergeUsage(orderUsage, recipeUsage);
    return getOrderRecommendations(prices, inventory, orderHistory, merged, { limit: 5 });
  }, [prices, inventory, orderHistory, categories]);

  const totalCost = recommendations.reduce((s, r) => s + (r.estimatedCost || 0), 0);

  const goToPurchase = () => onNavigate && onNavigate('purchase');
  const goToInventory = () => onNavigate && onNavigate('inventory');

  return (
    <div style={{
      background: WHITE, borderRadius: 18, padding: isMobile ? 16 : 22,
      border: '0.5px solid rgba(44,58,48,0.08)',
      boxShadow: '0 4px 16px rgba(44,58,48,0.05)',
      marginBottom: 18,
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: SAGE_BG, color: SAGE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>
            📦
          </div>
          <div>
            <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: SAGE_DARK }}>
              발주 추천 ({recommendations.length}건)
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
              재고·사용속도 기반 자동 분석
            </div>
          </div>
        </div>
        {recommendations.length > 0 && (
          <button
            onClick={goToPurchase}
            style={{
              padding: '7px 14px', background: SAGE, color: WHITE,
              border: 'none', borderRadius: 999, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            발주서 작성 →
          </button>
        )}
      </div>

      {/* 빈 상태 */}
      {recommendations.length === 0 && (
        <div style={{
          padding: '22px 16px', textAlign: 'center',
          background: '#FAFAFA', borderRadius: 12,
        }}>
          <div style={{ fontSize: 14, color: '#18181B', fontWeight: 700, marginBottom: 6 }}>
            발주 필요한 식재료가 없어요
          </div>
          <div style={{ fontSize: 11, color: '#71717A', lineHeight: 1.6 }}>
            재고가 안정적이거나 아직 데이터가 충분하지 않을 수 있어요.<br />
            <button
              onClick={goToInventory}
              style={{
                background: 'transparent', border: 'none', color: '#52525B',
                textDecoration: 'underline', cursor: 'pointer', fontSize: 11,
                padding: 0, marginTop: 4,
              }}
            >
              재고 관리에서 현재 재고 입력 →
            </button>
          </div>
        </div>
      )}

      {/* 추천 리스트 */}
      {recommendations.length > 0 && (
        <>
          <div style={{ display: 'grid', gap: 8 }}>
            {recommendations.map((r, i) => (
              <RecommendRow key={r.name + i} item={r} onClick={goToPurchase} />
            ))}
          </div>

          {/* 총합 */}
          {totalCost > 0 && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: WARM_BG, borderRadius: 10,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 12, color: MUTED }}>예상 발주 비용</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: CORAL_DARK }}>
                약 ₩{fmt(totalCost)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ───────────────────────── 추천 행 ─────────────────────────

function RecommendRow({ item, onClick }) {
  const urgent = item.urgency >= 2;
  const bg = urgent ? '#FCEAE5' : '#FFFAEC';
  const textColor = urgent ? CORAL_DARK : '#92400E';
  const days = Math.max(0, Math.floor(item.daysLeft || 0));

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px', background: bg, borderRadius: 12,
        cursor: 'pointer', transition: 'transform 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
      onMouseLeave={e => e.currentTarget.style.transform = ''}
    >
      {/* 긴급도 아이콘 */}
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: WHITE, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 16, flexShrink: 0,
      }}>
        {item.status.icon}
      </div>

      {/* 이름 + 상태 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: SAGE_DARK,
          marginBottom: 2, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: textColor, fontWeight: 500 }}>
          {item.currentStock <= 0 ? '재고 없음' : `${days}일 후 소진 예상`}
          {item.unit && ` · 현재 ${fmt(item.currentStock)}${item.unit}`}
        </div>
      </div>

      {/* 추천 수량 */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 10, color: MUTED, marginBottom: 1 }}>추천</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: SAGE }}>
          {fmt(item.recommendedQty)}{item.unit}
        </div>
        {item.estimatedCost > 0 && (
          <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>
            약 ₩{fmt(item.estimatedCost)}
          </div>
        )}
      </div>
    </div>
  );
}
